import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of, concat, EMPTY } from 'rxjs';
import moment from 'moment-timezone';
import { ApiService } from '../api/api.service';
import { map, tap } from "rxjs/operators";
import { PermissionsService } from '../permissions/permissions.service';

@Injectable({
  providedIn: 'root'
})
export class ReferencesService {
  private requestCache = new Map<string, { data: any, timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  statusMap = {
    'Requested': 'requested',
    'Deleted': 'deleted',
    'In progress': 'in_progress',
    'Expired': 'expired',
    'Completed': 'completed',
    'Stopped': 'stopped',
  }

  constructor(
    private apiService: ApiService,
    private permissionService: PermissionsService
  ) { }

  getReferencesV1(
    searchOption?: any,
    rowsPerPage = 5,
    pageNumber = 1,
    sortString?: any,
    filterOptions?: any,
  ): Observable<any> {
    const cacheKey = this.createCacheKey(searchOption, rowsPerPage, pageNumber, sortString, filterOptions);

    let params = new HttpParams();

    if (sortString) {
      params = params.set('sort', sortString);
    }

    if (rowsPerPage) {
      params = params.set('limit', String(rowsPerPage));
      params = params.set('page', String(pageNumber));
    }

    if (searchOption) {
      searchOption.forEach(element => {
        params = params.append(element.key, element.value)
      });
    }

    if (filterOptions) {
      if (filterOptions.filter != 'All') {
          let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
          let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
          params = params.set('start_date', utcStart.format())
          params = params.set('end_date', utcEnd.format());
        }
      if (filterOptions['statusOption'].length) {
        params = params.set(
          'states',
          filterOptions['statusOption'].map(status => this.statusMap[status]).join(','));
      }
      if (filterOptions?.['checkboxOption']?.includes('me')) {
        params = params.set('requester', '1');
      }
    }
    if (filterOptions?.['checkboxOption']?.includes('unread_only')) {
      params = params.set('unread_only', 'true');
    }

    const path = 'check/references/checks';

    // Create the API call observable
    const apiCall$ = this.apiService.get(path, { params }).pipe(
      map(res => res['data']),
      tap(data => {
        this.requestCache.set(cacheKey, {
          data: data,
          timestamp: Date.now()
        });
      })
    );

    // Stale-while-revalidate: return cached data first, then fresh data
    if (this.requestCache.has(cacheKey)) {
      const cached = this.requestCache.get(cacheKey);
      if (cached) {
        // Return cached data immediately, then fetch fresh data in background
        return concat(of(cached.data), apiCall$);
      }
    }

    // No cache - just make API call
    return apiCall$;
  }

  clearCache() {
    this.requestCache.clear();
  }

  private createCacheKey(search: any, rows: number, page: number, sort: string, filter: any): string {
    return JSON.stringify({ search, rows, page, sort, filter });
  }

  getReferences(
    searchOption?: any,
    rowsPerPage = 5,
    pageNumber = 1,
    sortString?: any,
    filterOptions?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (sortString) {
      params = params.set('sort', sortString);
    }

    if (rowsPerPage) {
      params = params.set('limit', String(rowsPerPage));
      params = params.set('page', String(pageNumber));
    }

    if (searchOption) {
      searchOption.forEach(element => {
        params = params.append(element.key, element.value)
      });
    }

    if (filterOptions) {
      if (filterOptions.filter != 'All') {
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('start_date', utcStart.format())
        params = params.set('end_date', utcEnd.format());
      }
      if (filterOptions['statusOption'].length) {
        params = params.set(
          'states',
          filterOptions['statusOption'].map(status => this.statusMap[status]).join(','));
      }
      if (filterOptions?.['checkboxOption']?.includes('me')) {
        params = params.set('requester', '1');
      }
    }

    const path = 'check/references/checks'
    return this.apiService.get(path, {params}).pipe(map(res => res['data']));
  }

  deprecatedGetReferences(
    searchOption?: any,
    rowsPerPage = 5,
    pageNumber = 1,
    sortString?: any,
    filterOptions?: any,
  ): Observable<any> {
    let params = new HttpParams();

    if (sortString) {
      params = params.set('sort', sortString);
    }

    if (rowsPerPage) {
      params = params.set('limit', String(rowsPerPage));
      params = params.set('page', String(pageNumber));
    }

    if (searchOption) {
      searchOption.forEach(element => {
        params = params.set(element.key, element.value);
      });
    }

    if (filterOptions) {
      if (filterOptions.filter != 'All') {
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        // params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
          params = params.set('start_date', utcStart.format())
          params = params.set('end_date', utcEnd.format());
        }
      if (filterOptions['statusOption'].length) {
        params = params.set(
          'states',
          filterOptions['statusOption'].map(status => this.statusMap[status]).join(','));
      }
      if (filterOptions?.['checkboxOption']?.includes('me')) {
        params = params.set('requester', '1');
      }
    }

    const path = 'check/references/checks'
    return this.apiService.get(path, { params }).pipe(map(res => res['data']));
  }

  // Makes a call to api.xref.io which in turn calls api-open's retrieve a check,
  // gets details of a reference check.
  getCheck(checkId) {
    const path = `check/references/check/${checkId}`;
    return this.apiService.get(path).pipe(map(res => res['data']));
  }

  // Checks whether the candidate is linked to a person in people_records
  getLinkedPerson(checkId, email) {
    let params = new HttpParams().set('check_id', checkId);
    params = params.set('email', email);
    const path = `check/references/check/linked_person_and_check`;
    return this.apiService.get(path, { params }).pipe(map(res => res['data']));
  }

  // Checks whether the candidate is linked to a person in people_records
  migratePerson(checkId, email, is_create_profile = 'false') {
    let params = new HttpParams().set('check_id', checkId);
    params = params.set('email', email);
    params = params.set('is_create_profile', is_create_profile)
    const path = `check/references/check/migrate`;
    return this.apiService.post(path, {}, { params }).pipe(map(res => res['data']));
  }

  deleteorstopCheck(checkId) {
    const path = `check/references/check/${checkId}`;
    return this.apiService.delete(path);
  }

  getCheckLifecycle(checkId) {
    const path = `check/references/check/${checkId}/lifecycle`;
    return this.apiService.get(path);
  }

  resendCandidateEmail(checkId) {
    if (!this.resendLinksEnabled()) {
      return EMPTY;
    }
    const path = `check/references/check/${checkId}/resend-candidate-email`;
    return this.apiService.post(path, {});
  }

  resendRefereeEmail(checkId, refereeId) {
    if (!this.resendLinksEnabled()) {
      return EMPTY;
    }
    const path = `check/references/check/${checkId}/referee/${refereeId}/resend-email`;
    return this.apiService.post(path, {});
  }

  private resendLinksEnabled(): boolean {
    return this.permissionService.isFeatureActivated(
      'ent-12120-enable-clients-to-resend-links-in-the-platform'
    ) === true;
  }
}
